import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mark } from '../components/brand/Mark';

/**
 * M5b — the public front door (`nelnela Landing.dc.html`, "Landing hero").
 *
 * Static: no API call, nothing that needs a session. It renders at `/` only for a visitor whose
 * `/api/me` came back 401; a signed-in learner gets Today at the same address.
 *
 * Three departures from the canvas, each deliberate:
 *   · **no "stories" / "method" nav links** — there is nothing to link to in P1, and a nav item
 *     that scrolls nowhere is worse than a shorter nav.
 *   · **no "scene 3 · 0:42" chip** — monospace on these canvases is annotation, not product UI.
 *   · **no "App splash" section** — that frame documents the brand for the design record; it is
 *     not something to say to a visitor.
 * And one addition: *How it works* needs somewhere to go, so there is a supporting band below
 * the hero. Its three lines are drawn from VISION.md's opening paragraph and claim nothing the
 * product does not already do.
 *
 * `noindex` (index.html) covers this page too — P1 is a single-user allowlist, and this is not
 * a public front door yet.
 */

const CONTAINER = {
  maxWidth: 1240,
  margin: '0 auto',
  padding: '0 48px',
  boxSizing: 'border-box' as const,
};

export function Landing() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'light-dark(var(--nn-bg), var(--mantine-color-dark-9))',
        color: 'var(--nn-text)',
      }}
    >
      <section style={{ ...CONTAINER, paddingBottom: 84 }}>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '26px 0' }}>
          <Mark size={27} />
          <span
            style={{
              font: "500 17px/1 'Noto Sans Georgian', sans-serif",
              letterSpacing: '-.01em',
              color: 'var(--nn-text-strong)',
            }}
          >
            nelnela
          </span>
          <span style={{ flex: 1 }} />
          <Link
            to="/signin"
            style={{
              fontSize: 13,
              color: 'color-mix(in srgb, var(--nn-text) 80%, var(--nn-muted))',
              border: '1px solid color-mix(in srgb, var(--nn-text) 16%, transparent)',
              borderRadius: 8,
              padding: '7px 14px',
              textDecoration: 'none',
            }}
          >
            sign in
          </Link>
        </nav>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 64,
            alignItems: 'center',
            paddingTop: 58,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div
              style={{
                font: "500 13.5px/1 'Noto Serif Georgian', serif",
                color: 'var(--nn-accent-text)',
                letterSpacing: '.03em',
              }}
            >
              <span lang="ka">ნელ-ნელა</span> · little by little
            </div>
            <h1
              style={{
                margin: 0,
                font: "600 clamp(34px, 5vw, 54px)/1.14 'Noto Serif Georgian', serif",
                letterSpacing: '-.01em',
                color: 'var(--nn-text-strong)',
                textWrap: 'pretty',
              }}
            >
              Little by little, the subtitles disappear.
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 16.5,
                lineHeight: 1.65,
                color: 'color-mix(in srgb, var(--nn-text) 65%, var(--nn-muted))',
                maxWidth: 470,
                textWrap: 'pretty',
              }}
            >
              nelnela teaches you a language from real scenes. Watch, listen again, understand a
              little more each time — until one day you notice you’ve stopped reading the
              translations.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                paddingTop: 6,
                flexWrap: 'wrap',
              }}
            >
              <Link
                to="/signin"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 9,
                  background: 'var(--nn-accent)',
                  color: 'var(--nn-on-accent)',
                  font: "600 15px/1 'Noto Sans Georgian', sans-serif",
                  borderRadius: 9,
                  padding: '14px 22px',
                  textDecoration: 'none',
                }}
              >
                Start with a scene
              </Link>
              <a
                href="#how"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  border: '1px solid color-mix(in srgb, var(--nn-text) 16%, transparent)',
                  color: 'color-mix(in srgb, var(--nn-text) 80%, var(--nn-muted))',
                  font: "500 15px/1 'Noto Sans Georgian', sans-serif",
                  borderRadius: 9,
                  padding: '13px 20px',
                  textDecoration: 'none',
                }}
              >
                How it works
              </a>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--nn-faint)' }}>
              Georgian first · new languages arrive little by little
            </div>
          </div>

          <HeroCard />
        </div>
      </section>

      <HowItWorks />
    </div>
  );
}

/**
 * The hero card. The well shows `public/landing-still.jpg` if Jeff has dropped one in, and
 * stays an empty dark well otherwise — never a placeholder graphic, never a stock frame.
 */
function HeroCard() {
  const [noStill, setNoStill] = useState(false);
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid var(--nn-hairline)',
        background: 'var(--nn-bg)',
        boxShadow: 'var(--mantine-shadow-lg)',
      }}
    >
      <div
        style={{
          aspectRatio: '16 / 9',
          position: 'relative',
          background: 'var(--mantine-color-dark-9)',
        }}
      >
        {!noStill && (
          <img
            src="/landing-still.jpg"
            alt=""
            onError={() => setNoStill(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
      </div>
      {/* Not a control — the app never draws playback chrome it does not own. It is a still of
          one, at the moment the caption below was said. */}
      <div
        aria-hidden
        style={{
          height: 3,
          background: 'color-mix(in srgb, var(--nn-text) 12%, transparent)',
          position: 'relative',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: 3,
            width: '38%',
            background: 'var(--nn-accent)',
          }}
        />
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: '38%',
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: 'var(--nn-accent)',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
      <div
        style={{
          padding: '16px 26px 19px',
          background:
            'light-dark(var(--nn-panel), color-mix(in srgb, var(--nn-bg) 60%, var(--mantine-color-dark-9)))',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <div
          lang="ka"
          style={{
            font: "600 20px/1.35 'Noto Serif Georgian', serif",
            color: 'var(--nn-text-strong)',
          }}
        >
          მოდი, დავიწყოთ.
        </div>
        {/* At .5 — the translation is already fading, which is the whole promise above it. */}
        <div style={{ fontSize: 14, lineHeight: 1.3, color: 'var(--nn-muted)', opacity: 0.5 }}>
          come on — let’s begin.
        </div>
      </div>
    </div>
  );
}

/**
 * Where *How it works* goes. Not on the canvas — the canvas's second section is brand
 * documentation — so it is built in the canvas's own idiom: same container, same top hairline,
 * same band colour as the splash section. The three lines are VISION.md's opening paragraph,
 * split into its three claims and no more.
 */
function HowItWorks() {
  const points: Array<{ head: string; body: string }> = [
    {
      head: 'Media you actually want to watch',
      body: 'Real Georgian television from official channels, embedded, with the transcript beside it. Tap any word for a gloss.',
    },
    {
      head: 'It works out what you know',
      body: 'From what you look up, replay and dwell on — not from grading yourself. There are no quizzes standing between you and the next scene.',
    },
    {
      head: 'A little every day, across all four skills',
      body: 'Reading, listening, speaking and writing, in small daily practice rather than long sessions you have to find time for.',
    },
  ];
  return (
    <section
      id="how"
      style={{
        borderTop: '1px solid var(--nn-hairline)',
        background:
          'light-dark(var(--nn-panel), color-mix(in srgb, var(--nn-bg) 60%, var(--mantine-color-dark-9)))',
      }}
    >
      <div
        style={{
          ...CONTAINER,
          padding: '64px 48px 80px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 40,
        }}
      >
        {points.map((p) => (
          <div key={p.head} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div
              style={{
                font: "600 19px/1.3 'Noto Serif Georgian', serif",
                color: 'var(--nn-text-strong)',
                textWrap: 'pretty',
              }}
            >
              {p.head}
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.65,
                color: 'color-mix(in srgb, var(--nn-text) 65%, var(--nn-muted))',
                textWrap: 'pretty',
              }}
            >
              {p.body}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
